import { ChangeDetectorRef, Component, OnInit, AfterViewInit } from '@angular/core';
import { MatTabChangeEvent } from '@angular/material/tabs';
import { ActivatedRoute, Router } from '@angular/router';
import { KEYBOARD_SHORTCUTS } from '@app-core/constants/keyboard-shortcuts.constants';
import { DataService } from '@app-core/services/data/data.service';
import { GoogleTagManagerService } from '@app-core/services/google-tag-manager/google-tag-manager.service';
import { KeyboardShortcutsService } from '@app-core/services/keyboard-shortcuts/keyboard-shortcuts.service';
import { Subject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { AuthGuard } from '@app-core/guards/auth/auth.guard';
import { MixPanelService } from '@app-core/services/mixpanel/mix-panel.service';

const tabNameIndexMapping = {
  overview: 0,
  manage: 1,
  devices: 2,
  diagnostics: 3,
};
@Component({
  selector: 'app-assets',
  templateUrl: './assets.component.html',
  styleUrls: ['./assets.component.scss'],
})
export class AssetsComponent implements OnInit, AfterViewInit {
  public keyboardShortcuts = KEYBOARD_SHORTCUTS;
  public currentOS = 'windows';
  public currentTabIndex = 0;
  public fleetId: string;
  private ngUnsubscribe: Subject<void> = new Subject<void>();
  private subTabDiagnostics: string = '';

  constructor(
    private dataService: DataService,
    private router: Router,
    private authGuard: AuthGuard,
    private route: ActivatedRoute,
    private keyboardShortcutsService: KeyboardShortcutsService,
    private cdr: ChangeDetectorRef,
    private gtmService: GoogleTagManagerService,
    private mixpanelService: MixPanelService
  ) {}

  ngOnInit() {
    this.dataService._currentFleet.pipe(takeUntil(this.ngUnsubscribe), skip(1)).subscribe((value: string) => {
      if (value) {
        this.fleetId = value;
        this.router.navigate([], {
          queryParams: { tab: 'overview' }, // Ensure queryParams reflect the first tab
        });
      }
    });

    this.currentOS = this.dataService.getCurrentOS();
    this.configureKeyboardShortcuts();
  }

  public ngAfterViewInit(): void {
    this.route.queryParams.pipe(takeUntil(this.ngUnsubscribe)).subscribe((param: any) => {
      const currentTab = param.tab;
      this.subTabDiagnostics = param.subTab || 'cameraevent';
      this.currentTabIndex = tabNameIndexMapping[currentTab] || 0;
      switch (this.currentTabIndex) {
        case 0:
          this.gtmService.customTabs('/assets', 'Assets', 'Assets Overview');
          break;
        case 1:
          this.gtmService.customTabs('/assets', 'Assets', 'Manage Assets');
          break;
        case 2:
          this.gtmService.customTabs('/assets', 'Assets', 'Devices');
          break;
        case 3:
          this.gtmService.customTabs('/diagnostics', 'Diagnostics', 'diagnostics');
          break;
        default:
          this.gtmService.customTabs('/assets', 'Assets', 'Devices');
          break;
      }
      this.cdr.detectChanges();
    });
  }

  public onPageTabChange(event: MatTabChangeEvent) {
    let tabName = event.tab.textLabel.toString() || 'overview';
    let queryParams: any;

    if (tabName === 'diagnostics') {
      const { subTab = null, eventType = null, deviceId = null } = this.route.snapshot.queryParams || {};
      queryParams = {
        tab: tabName,
        ...(subTab ? { subTab } : { subTab: this.subTabDiagnostics }),
        ...(eventType ? { eventType } : {}),
        ...(deviceId ? { deviceId } : {}),
      };
    } else {
      queryParams = {
        tab: tabName,
      };
    }

    this.router.navigate([], {
      queryParams,
    });

    if (!this.authGuard.isRouteAccessible('assets')) {
      this.router.navigate(['home']); // Redirect to home if not accessible
    }
    let displayTabName = '';
    if (tabName === 'overview') {
      displayTabName = 'Overview Tab Clicked - Assets Page';
    } else if (tabName === 'manage') {
      displayTabName = 'Manage Assets Tab Clicked - Assets Page';
    } else if (tabName === 'devices') {
      displayTabName = 'Devices Tab Clicked - Assets Page';
    } else {
      displayTabName = 'Diagnostics Tab Clicked - Assets Page';
    }
    this.mixpanelService.track(`${displayTabName}`, {
      'Tab Name': tabName,
    });
  }

  public configureKeyboardShortcuts() {
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab0[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(0);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab1[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(1);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab2[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(2);
      });
    this.keyboardShortcutsService
      .addShortcut({ keys: this.keyboardShortcuts.goToTab3[this.currentOS] })
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.onKeyboardTabChange(3);
      });
  }

  public onKeyboardTabChange(tabIndex: number) {
    const [tabName = 'overview'] = Object.entries(tabNameIndexMapping).find(([, idx]) => idx === tabIndex);
    this.router.navigate([], {
      queryParams: {
        tab: tabName,
      },
    });
    let displayTabName = '';
    if (tabName === 'overview') {
      displayTabName = 'Overview Tab Clicked - Assets Page';
    } else if (tabName === 'manage') {
      displayTabName = 'Manage Assets Tab Clicked - Assets Page';
    } else if (tabName === 'devices') {
      displayTabName = 'Device Tab Clicked - Assets Page';
    } else {
      displayTabName = 'Diagnostics Tab Clicked - Assets Page';
    }
    this.mixpanelService.track(`${displayTabName}`, {
      'Tab Name': tabName,
    });
  }
}
